import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../lib/api';
import { toast } from 'sonner';
import FileUploader from './FileUploader';
import BuildingTypesManager from './BuildingTypesManager';
import DangerZone from './DangerZone';

const SettingsTab = () => {
  const [settings, setSettings] = useState({
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    about_text: '',
    terms_file_id: null,
    privacy_file_id: null,
    terms_conditions_file_id: null,
    refund_policy_file_id: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      setSettings({ ...settings, ...response.data });
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateSettings(settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading settings...</div>;
  }

  return (
    <div className="space-y-8" data-testid="settings-tab">
      {/* Contact Details */}
      <section className="bg-white border border-border p-6">
        <h3 className="text-lg font-bold text-primary mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Contact Details
        </h3>
        <p className="text-sm text-text_muted mb-4">These details appear across the e-commerce store (footer, checkout, invoices).</p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Contact Email</label>
            <input
              type="email"
              value={settings.contact_email || ''}
              onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
              className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
              data-testid="contact-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contact Phone</label>
            <input
              type="text"
              value={settings.contact_phone || ''}
              onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
              className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Contact Address</label>
            <textarea
              value={settings.contact_address || ''}
              onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
              rows="3"
              className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
              data-testid="contact-address-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">About Text (Footer)</label>
            <textarea
              value={settings.about_text || ''}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              rows="3"
              className="w-full p-2 border border-border rounded focus:ring-2 focus:ring-accent"
              data-testid="about-text-input"
            />
          </div>
        </div>
      </section>

      {/* Legal Documents */}
      <section className="bg-white border border-border p-6">
        <h3 className="text-lg font-bold text-primary mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Legal Documents
        </h3>
        <p className="text-sm text-text_muted mb-4">Upload legal PDFs. Links will appear in the footer.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2">Terms of Service</h4>
            <FileUploader
              accept=".pdf"
              currentFileId={settings.terms_file_id}
              currentFilename={settings.terms_file_id ? "Terms of Service.pdf" : null}
              onUploaded={(data) => setSettings({ ...settings, terms_file_id: data.file_id })}
              onCleared={() => setSettings({ ...settings, terms_file_id: null })}
              testId="upload-terms"
            />
          </div>
          <div>
            <h4 className="font-medium mb-2">Privacy Policy</h4>
            <FileUploader
              accept=".pdf"
              currentFileId={settings.privacy_file_id}
              currentFilename={settings.privacy_file_id ? "Privacy Policy.pdf" : null}
              onUploaded={(data) => setSettings({ ...settings, privacy_file_id: data.file_id })}
              onCleared={() => setSettings({ ...settings, privacy_file_id: null })}
              testId="upload-privacy"
            />
          </div>
          <div>
            <h4 className="font-medium mb-2">Terms and Conditions</h4>
            <FileUploader
              accept=".pdf"
              currentFileId={settings.terms_conditions_file_id}
              currentFilename={settings.terms_conditions_file_id ? "Terms and Conditions.pdf" : null}
              onUploaded={(data) => setSettings({ ...settings, terms_conditions_file_id: data.file_id })}
              onCleared={() => setSettings({ ...settings, terms_conditions_file_id: null })}
              testId="upload-terms-conditions"
            />
          </div>
          <div>
            <h4 className="font-medium mb-2">Refund Policy</h4>
            <FileUploader
              accept=".pdf"
              currentFileId={settings.refund_policy_file_id}
              currentFilename={settings.refund_policy_file_id ? "Refund Policy.pdf" : null}
              onUploaded={(data) => setSettings({ ...settings, refund_policy_file_id: data.file_id })}
              onCleared={() => setSettings({ ...settings, refund_policy_file_id: null })}
              testId="upload-refund-policy"
            />
          </div>
        </div>
      </section>

      {/* Building Types */}
      <BuildingTypesManager />

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-accent text-white rounded font-bold hover:bg-opacity-90 disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Danger Zone: Database cleanup */}
      <DangerZone />
    </div>
  );
};

export default SettingsTab;
